/**
 * The order page for the purchase process, v76 (docs/ORDER_PAGE_MATRIX.md):
 * for each state and side, a headline, one line saying what happens next and
 * by when, and the buttons — every one a transition Sharetribe offers this
 * person now (`nextTransitions`). Nothing here reads money from metadata; the
 * breakdown shows the line items.
 *
 * Deadlines are the ones the server's steps stamped on the order
 * (server/api-util/deadline-rules.js); the page only shows them.
 *
 * An order from before v76 keeps no ledger: it shows "an older format"
 * message and no buttons.
 */
import { DEFAULT_RETURN_POLICY } from '../../util/dataExtractors';
import {
  cartItemsOf,
  returnedItems,
  returnHistoryOf,
  defectWindowDays,
} from '../../util/returnCycles';
import { addBusinessDays } from '../../util/businessDays';
import { OFFER_HOURS } from '../../util/offerWindow';
import { transitions as T } from '../../transactions/transactionProcessPurchase';
import { canVoidLabel } from './orderLabel';
import {
  PAID_TRANSITIONS,
  RECEIVED_TRANSITIONS,
  RELIST,
  SHIPPED_TRANSITIONS,
} from './orderTransitions';

const CUSTOMER = 'customer';
const PROVIDER = 'provider';
const DAY_MS = 24 * 60 * 60 * 1000;

// The delivery range, as server/api-util/order-steps/steps/delivery.js
// decides it: the carrier's estimate to 2 business days after it, else 3 to 8
// business days after shipping.
const ESTIMATE_SPREAD_BUSINESS_DAYS = 2;
const DEFAULT_TRANSIT_BUSINESS_DAYS = { from: 3, to: 8 };
// An order without its own ship-by date is due this many days after it was
// paid, as the server's cancellations count it (steps/cancels.js shipBy;
// orderRules.parity.test.js).
const DEFAULT_SHIP_BY_DAYS = 14;

/**
 * The buyer's return, by the state it is opened from: the server's
 * REQUEST_BY_STATE (steps/returns.js; orderRules.parity.test.js).
 */
export const CUSTOMER_RETURN_TRANSITION_BY_STATE = Object.freeze({
  fulfilled: T.CUSTOMER_REQUEST_RETURN,
  completed: T.CUSTOMER_RETURN_FROM_COMPLETED,
  reviewed: T.CUSTOMER_RETURN_FROM_REVIEWED,
  'reviewed-by-customer': T.CUSTOMER_RETURN_FROM_REVIEWED_BY_CUSTOMER,
  'reviewed-by-provider': T.CUSTOMER_RETURN_FROM_REVIEWED_BY_PROVIDER,
});

// ─────────────────────────── reading the order ───────────────────────────

const dataOf = transaction => transaction?.attributes?.protectedData || {};
const metaOf = transaction => transaction?.attributes?.metadata || {};
const historyOf = transaction => transaction?.attributes?.transitions || [];
const firstAt = (transaction, names) =>
  historyOf(transaction).find(t => names.includes(t.transition))?.createdAt || null;
const iso = date => (date ? new Date(date).toISOString() : null);
const plus = (date, ms) => (date ? new Date(new Date(date).getTime() + ms) : null);

/** Does the open return have a label bought on TradMarket (by either side)? */
export const hasReturnLabel = transaction => !!dataOf(transaction).returnLabelPayment?.labelId;

/** Is this an order of the ledger (v76 on)? Older orders keep none. */
export const isLedgerOrder = transaction => Array.isArray(dataOf(transaction).ledgerEvents);

/**
 * The return policy snapshotted at purchase. A cart's items each carry their
 * own: returns are open while any item accepts them, for its longest window.
 */
export const getTransactionReturnPolicy = transaction => {
  const saved = dataOf(transaction).returnPolicy || {};
  const items = cartItemsOf(transaction);
  const perItem = items.length > 1 ? items.map(item => item?.returnPolicy).filter(Boolean) : [];
  const returnable = perItem.filter(policy => policy.acceptsReturns !== false);
  const windowOf = policy =>
    Number.isFinite(policy.windowDays) ? policy.windowDays : DEFAULT_RETURN_POLICY.windowDays;
  return {
    acceptsReturns: perItem.length
      ? returnable.length > 0
      : typeof saved.acceptsReturns === 'boolean'
      ? saved.acceptsReturns
      : DEFAULT_RETURN_POLICY.acceptsReturns,
    windowDays: returnable.length ? Math.max(...returnable.map(windowOf)) : windowOf(saved),
    returnShippingPaidBy:
      saved.returnShippingPaidBy === 'seller'
        ? 'seller'
        : DEFAULT_RETURN_POLICY.returnShippingPaidBy,
    restockingFeePercent: Number.isFinite(saved.restockingFeePercent)
      ? saved.restockingFeePercent
      : DEFAULT_RETURN_POLICY.restockingFeePercent,
  };
};

/** When the buyer got the order: the carrier's delivery scan, else when it was marked received. */
export const deliveredAt = transaction =>
  metaOf(transaction).deliveredAt || firstAt(transaction, RECEIVED_TRANSITIONS);

const validDate = value => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};
// A carrier's date is a calendar day: midday Eastern that day, the same day all over the US.
const carrierDay = date => new Date(`${date.toISOString().slice(0, 10)}T17:00:00.000Z`);

/**
 * When the order should arrive (eBay's and Reverb's "Arrives Oct 3–6"):
 * { from, to } as ISO strings, or null before it ships. From the carrier's
 * estimate (the tracker's, else the label's date, else its days in transit
 * from shipping) to two business days after; with none, 3 to 8 business days
 * after shipping. The same as the server's (deliveryWindow.parity.test.js).
 */
export const deliveryWindow = transaction => {
  const metadata = metaOf(transaction);
  const label = metadata.shippingLabel || {};
  const shipped = validDate(firstAt(transaction, SHIPPED_TRANSITIONS));
  const carrierDate = validDate(metadata.trackingEstDeliveryDate) || validDate(label.deliveryDate);
  const transitDays = Number(label.estimatedDeliveryDays);
  const estimate = carrierDate
    ? carrierDay(carrierDate)
    : shipped && transitDays > 0
    ? addBusinessDays(shipped, transitDays)
    : null;
  if (estimate)
    return {
      from: iso(estimate),
      to: iso(addBusinessDays(estimate, ESTIMATE_SPREAD_BUSINESS_DAYS)),
    };
  if (!shipped) return null;
  return {
    from: iso(addBusinessDays(shipped, DEFAULT_TRANSIT_BUSINESS_DAYS.from)),
    to: iso(addBusinessDays(shipped, DEFAULT_TRANSIT_BUSINESS_DAYS.to)),
  };
};

/** When the order should have arrived by: the end of its delivery window ("item hasn't arrived" opens after it). */
export const expectedBy = transaction => deliveryWindow(transaction)?.to || null;

/** The seller's ship-by date: the order's own, else paid + 14 days. */
export const shipBy = transaction => {
  const own = dataOf(transaction).shipBy;
  if (own) return iso(own);
  const paid = firstAt(transaction, PAID_TRANSITIONS);
  return paid ? iso(plus(paid, DEFAULT_SHIP_BY_DAYS * DAY_MS)) : null;
};

/**
 * The buyer's return windows, from delivery (an operator may extend both):
 * a change of mind by the seller's policy, a defect by each item's defect
 * window (returnCycles.js defectWindowDays): open while any item's is.
 */
export const returnWindows = (transaction, now = new Date()) => {
  const policy = getTransactionReturnPolicy(transaction);
  const delivered = deliveredAt(transaction);
  if (!delivered)
    return {
      policy,
      preferenceEndsAt: null,
      defectEndsAt: null,
      preferenceOpen: false,
      defectOpen: false,
    };
  const extension = metaOf(transaction).returnWindowExtendedUntil
    ? new Date(metaOf(transaction).returnWindowExtendedUntil)
    : null;
  const later = date =>
    extension && !Number.isNaN(extension.getTime()) && extension > date ? extension : date;
  const preferenceEndsAt = later(plus(delivered, policy.windowDays * DAY_MS));
  // As the server counts it (returns-record.js defectWindowDays), per item:
  // the longest any item of the order still has.
  // Items already returned can't go back again, so their windows don't count.
  const saved = dataOf(transaction).returnPolicy || {};
  const returned = returnedItems(transaction);
  const items = cartItemsOf(transaction);
  const open = items.filter(item => !returned.has(item?.listingId));
  const itemPolicies = items.length ? open.map(item => item?.returnPolicy || saved) : [saved];
  const defectDays = itemPolicies.length ? Math.max(...itemPolicies.map(defectWindowDays)) : 0;
  const defectEndsAt = later(plus(delivered, defectDays * DAY_MS));
  return {
    policy,
    preferenceEndsAt: iso(preferenceEndsAt),
    defectEndsAt: iso(defectEndsAt),
    preferenceOpen: policy.acceptsReturns && now <= preferenceEndsAt,
    defectOpen: now <= defectEndsAt,
  };
};

/**
 * The current or last return, in the shape the shipping card and the return
 * details modal read: its type and reason, how it travels, and the returns
 * before it.
 */
const returnDetails = transaction => {
  const data = dataOf(transaction);
  const history = Array.isArray(data.returnHistory) ? data.returnHistory : [];
  if (!data.returnId && !history.length) return null;
  const shipping = getReturnShipping(transaction);
  const tracking = shipping.label || shipping.tracking || {};
  // The carrier's scans of this return's parcel (an earlier return's label
  // had its own), kept by the EasyPost webhook.
  const metadata = metaOf(transaction);
  const scansOfThisLabel =
    !!tracking.trackingCode && metadata.returnTrackingEventsFor === tracking.trackingCode;
  return {
    returnType: data.returnType || null,
    returnReason: data.returnReason || null,
    trackingCode: tracking.trackingCode || null,
    trackingUrl: tracking.trackingUrl || null,
    carrier: tracking.carrier || null,
    labelUrl: shipping.label?.labelUrl || null,
    labelCost: shipping.label ? shipping.label.cost : null,
    labelPaidBy: shipping.label?.paidBy || null,
    returnItems: Array.isArray(data.returnItems) ? data.returnItems : null,
    // What the buyer said and showed, and the seller's rejection: each side
    // sees the other's (TradMarket reads the same keys).
    returnDescription: data.returnDescription || null,
    returnPhotos: Array.isArray(data.returnPhotos) ? data.returnPhotos : [],
    sellerRejection:
      data.returnSellerReview?.decision === 'rejected'
        ? {
            reason: data.returnSellerReview.reason || null,
            photos: Array.isArray(data.returnSellerReview.photos)
              ? data.returnSellerReview.photos
              : [],
          }
        : null,
    returnHistory: history,
    trackingEvents:
      scansOfThisLabel && Array.isArray(metadata.returnTrackingEvents)
        ? metadata.returnTrackingEvents
        : [],
  };
};

/**
 * Is anything still with the buyer to send back? Items go back for good with a return
 * refunded or a rejection upheld (returnCycles.returnedItems), a single item as much as
 * a cart's.
 */
const itemsLeftToReturn = transaction => {
  const returned = returnedItems(transaction);
  const items = cartItemsOf(transaction);
  if (!items.length) return returned.size === 0;
  return items.some(item => !returned.has(item.listingId));
};

// A return settled for good: its items are no longer the buyer's.
const SETTLED_OUTCOMES = ['refunded', 'upheld'];

/**
 * Has a refunded return brought items back that the seller hasn't put back
 * in stock yet? Each return once (metadata.relisted, by returnId), and only
 * one that brought something back, as the server decides it
 * (server/api-util/order-relist.js returnsToRelist; orderRules.parity.test.js).
 * A return stored without its items covered everything still with the buyer.
 */
export const canRelist = transaction => {
  const data = dataOf(transaction);
  const done = metaOf(transaction).relisted || {};
  const cart = cartItemsOf(transaction);
  const mainListingId = transaction?.listing?.id?.uuid || null;
  const lines = cart.length ? cart : mainListingId ? [{ listingId: mainListingId }] : [];
  const current = data.returnId
    ? [{ returnId: data.returnId, outcome: data.returnOutcome, items: data.returnItems }]
    : [];
  const gone = new Set();
  const toRelist = [];
  [...returnHistoryOf(transaction), ...current].forEach(cycle => {
    if (!cycle?.returnId || !SETTLED_OUTCOMES.includes(cycle.outcome)) return;
    const items = Array.isArray(cycle.items)
      ? cycle.items
      : lines.filter(line => !gone.has(line.listingId));
    items.forEach(item => gone.add(item?.listingId));
    const broughtBack = items.filter(item => item?.listingId).length > 0;
    if (cycle.outcome === 'refunded' && broughtBack && !done[cycle.returnId]) {
      toRelist.push(cycle.returnId);
    }
  });
  return toRelist.length > 0;
};

/**
 * How the current return gets back to the seller, for the order page's
 * "Return shipping" card: its label, where to send it, and once the label
 * shows it delivered, the seller's inspection deadline.
 */
export const getReturnShipping = transaction => {
  const data = dataOf(transaction);
  const metadata = metaOf(transaction);
  const payment = hasReturnLabel(transaction) ? data.returnLabelPayment : null;
  // The label's printable copy: kept in metadata when it was bought.
  const shown =
    payment && metadata.returnLabel?.labelId === payment.labelId ? metadata.returnLabel : null;
  const seller =
    metadata.shippingLabel?.fromAddress ||
    data.shippingParams?.shippingAddress ||
    transaction?.listing?.attributes?.publicData?.shippingAddress ||
    null;
  const returnAddress =
    seller?.street1 || seller?.line1
      ? {
          name: seller.name || null,
          line1: seller.street1 || seller.line1 || null,
          line2: seller.street2 || seller.line2 || null,
          city: seller.city || null,
          state: seller.state || null,
          postalCode: seller.zip || seller.postal_code || null,
          country: seller.country || null,
        }
      : null;
  const opened = data.returnOpenedAt ? new Date(data.returnOpenedAt) : null;
  const delivered = metadata.returnDeliveredAt ? new Date(metadata.returnDeliveredAt) : null;
  const deliveredThisReturn = delivered && (!opened || delivered >= opened);
  return {
    returnType: data.returnType || null,
    sellerCovers: data.returnShippingPaidBy === 'seller',
    label: payment
      ? {
          labelUrl: shown?.labelUrlPdf || shown?.labelUrl || null,
          carrier: payment.carrier || null,
          service: payment.service || null,
          trackingCode: payment.trackingCode || null,
          trackingUrl: shown?.trackingUrl || null,
          cost: (payment.costCents || 0) / 100,
          paidBy: payment.paidBy,
        }
      : null,
    tracking: data.returnTrackingNumber
      ? {
          trackingCode: data.returnTrackingNumber,
          trackingUrl: data.returnTrackingUrl || null,
          carrier: data.returnCarrier || null,
        }
      : null,
    returnAddress,
    deliveredAt: deliveredThisReturn ? metadata.returnDeliveredAt : null,
    inspectionDeadline: deliveredThisReturn ? metadata.returnInspectionDeadline || null : null,
  };
};

// ─────────────────────────── the table ───────────────────────────

/**
 * Per state and side: the buttons in the order they show (each only when
 * Sharetribe offers it, and its `when` allows), and the next-step line.
 *
 * nextStep(transaction) → { id, dates, days, values } | null, ids under
 * OrderNextStep.<state>.<side>: dates show with their time, days without.
 */
// The carrier statuses that need someone to act (the EasyPost webhook emails
// both sides and TradMarket about each): the line says so until delivery.
const DELIVERY_PROBLEMS = {
  failure: 'Failure',
  return_to_sender: 'ReturnToSender',
  available_for_pickup: 'AwaitingPickup',
};
const deliveryProblem = transaction =>
  metaOf(transaction).deliveredAt
    ? null
    : DELIVERY_PROBLEMS[metaOf(transaction).trackingStatus] || null;
// The delivery range, as days (no time of day) for the next-step line.
const arrivalDays = transaction => {
  const range = deliveryWindow(transaction);
  return { arrivesFrom: range?.from || null, arrivesTo: range?.to || null };
};

// Money went back to the buyer on this order (a cancellation after paying, a return).
const refundedToBuyer = transaction =>
  (transaction?.attributes?.lineItems || []).some(
    line => !line.reversal && /^line-item\/(refund-|goodwill-refund)/.test(line.code)
  );

// The last offer is still open: before its stamped expiry (else its time + the
// window, as the server counts it). Past it, answering is refused (offers.js).
const offerOpen = transaction => {
  const offers = Array.isArray(dataOf(transaction).offers) ? dataOf(transaction).offers : [];
  const last = offers[offers.length - 1];
  const expires = last?.expiresAt
    ? Date.parse(last.expiresAt)
    : last?.at
    ? Date.parse(last.at) + OFFER_HOURS * 60 * 60 * 1000
    : NaN;
  return !Number.isFinite(expires) || Date.now() <= expires;
};

const offerLine = (state, side) => transaction => {
  const offers = Array.isArray(dataOf(transaction).offers) ? dataOf(transaction).offers : [];
  const last = offers[offers.length - 1];
  return { id: `OrderNextStep.${state}.${side}`, dates: { expires: last?.expiresAt || null } };
};

const TABLE = {
  'offer-sent': {
    [PROVIDER]: {
      buttons: ['transition/accept-offer', 'transition/counter-offer', 'transition/decline-offer'],
      when: { 'transition/accept-offer': offerOpen, 'transition/counter-offer': offerOpen },
      nextStep: offerLine('offer-sent', 'seller'),
    },
    [CUSTOMER]: {
      buttons: ['transition/customer-withdraw-offer'],
      nextStep: offerLine('offer-sent', 'buyer'),
    },
  },
  'offer-countered': {
    [CUSTOMER]: {
      buttons: [
        'transition/accept-after-counter-offer',
        'transition/customer-counter-offer',
        'transition/decline-after-counter-offer',
      ],
      when: {
        'transition/accept-after-counter-offer': offerOpen,
        'transition/customer-counter-offer': offerOpen,
      },
      nextStep: offerLine('offer-countered', 'buyer'),
    },
    [PROVIDER]: { buttons: [], nextStep: offerLine('offer-countered', 'seller') },
  },
  'offer-accepted': {
    // The buyer pays through checkout, on this order (request-payment-after-offer).
    [CUSTOMER]: {
      buttons: ['transition/request-payment-after-offer'],
      nextStep: t => ({
        id: 'OrderNextStep.offer-accepted.buyer',
        dates: { payBy: dataOf(t).agreedOffer?.payBy || null },
      }),
    },
    [PROVIDER]: {
      buttons: [],
      nextStep: t => ({
        id: 'OrderNextStep.offer-accepted.seller',
        dates: { payBy: dataOf(t).agreedOffer?.payBy || null },
      }),
    },
  },
  'pending-payment': {
    [CUSTOMER]: { buttons: [], nextStep: () => ({ id: 'OrderNextStep.pending-payment.buyer' }) },
    [PROVIDER]: { buttons: [], isPendingPayment: true },
  },
  purchased: {
    [PROVIDER]: {
      buttons: [
        'transition/mark-shipped',
        'transition/mark-picked-up',
        'transition/provider-buy-label',
        'transition/provider-void-label',
        'transition/provider-cancel',
      ],
      when: {
        'transition/mark-shipped': t => dataOf(t).deliveryMethod !== 'pickup',
        'transition/mark-picked-up': t => dataOf(t).deliveryMethod === 'pickup',
        // One button to get a label: the wizard offers card or earnings.
        'transition/provider-buy-label': t =>
          dataOf(t).deliveryMethod !== 'pickup' && !dataOf(t).shippingLabelPayment,
        // As the server's void step allows it (orderLabel.js canVoidLabel)
        'transition/provider-void-label': t => canVoidLabel(t),
      },
      nextStep: t =>
        dataOf(t).deliveryMethod === 'pickup'
          ? { id: 'OrderNextStep.purchased.sellerPickup' }
          : {
              id: dataOf(t).shippingLabelPayment
                ? 'OrderNextStep.purchased.sellerLabel'
                : 'OrderNextStep.purchased.seller',
              dates: { shipBy: shipBy(t) },
            },
    },
    [CUSTOMER]: {
      buttons: [
        'transition/customer-confirm-pickup',
        'transition/customer-cancel-late',
        'transition/customer-request-cancel',
      ],
      when: {
        'transition/customer-confirm-pickup': t => dataOf(t).deliveryMethod === 'pickup',
        // Once the ship-by date has passed the buyer cancels outright; before, they ask.
        'transition/customer-cancel-late': t => !!shipBy(t) && new Date() > new Date(shipBy(t)),
        'transition/customer-request-cancel': t => !shipBy(t) || new Date() <= new Date(shipBy(t)),
      },
      nextStep: t =>
        dataOf(t).deliveryMethod === 'pickup'
          ? { id: 'OrderNextStep.purchased.buyerPickup' }
          : {
              id:
                new Date() > new Date(shipBy(t))
                  ? 'OrderNextStep.purchased.buyerLate'
                  : 'OrderNextStep.purchased.buyer',
              dates: { shipBy: shipBy(t) },
            },
    },
  },
  'cancel-requested': {
    [PROVIDER]: {
      buttons: ['transition/provider-accept-cancel', 'transition/provider-decline-cancel'],
      // The buyer's reason, as eBay and Reverb show it to the seller deciding.
      nextStep: t => ({
        id: 'OrderNextStep.cancel-requested.seller',
        dates: { answerBy: dataOf(t).cancelRequest?.answerBy || null },
        values: {
          hasReason: dataOf(t).cancelRequest?.reason ? 'yes' : 'no',
          reason: dataOf(t).cancelRequest?.reason || '',
        },
      }),
    },
    [CUSTOMER]: {
      buttons: [],
      nextStep: t => ({
        id: 'OrderNextStep.cancel-requested.buyer',
        dates: { answerBy: dataOf(t).cancelRequest?.answerBy || null },
      }),
    },
  },
  fulfilled: {
    [CUSTOMER]: {
      buttons: [
        'transition/mark-received',
        'transition/customer-request-return',
        'transition/customer-report-not-received',
        // No buyer "Dispute" here (as eBay and Reverb): before delivery a
        // problem is "item hasn't arrived" (not-received, which escalates to
        // TradMarket); once delivered it is a return, which TradMarket
        // decides if the seller rejects it.
      ],
      when: {
        // A return opens once the buyer has the order (Reverb, Etsy, eBay).
        'transition/customer-request-return': t => !!metaOf(t).deliveredAt && returnOpen(t),
        // Only once it is late.
        'transition/customer-report-not-received': t =>
          !metaOf(t).deliveredAt && !!expectedBy(t) && new Date() > new Date(expectedBy(t)),
      },
      nextStep: t =>
        metaOf(t).deliveredAt
          ? { id: 'OrderNextStep.fulfilled.buyerDelivered' }
          : deliveryProblem(t)
          ? { id: `OrderNextStep.fulfilled.buyer${deliveryProblem(t)}` }
          : expectedBy(t) && new Date() > new Date(expectedBy(t))
          ? { id: 'OrderNextStep.fulfilled.buyerLate', days: arrivalDays(t) }
          : { id: 'OrderNextStep.fulfilled.buyer', days: arrivalDays(t) },
    },
    [PROVIDER]: {
      buttons: [],
      nextStep: t =>
        metaOf(t).deliveredAt
          ? { id: 'OrderNextStep.fulfilled.sellerDelivered' }
          : deliveryProblem(t)
          ? { id: `OrderNextStep.fulfilled.seller${deliveryProblem(t)}` }
          : { id: 'OrderNextStep.fulfilled.seller', days: arrivalDays(t) },
    },
  },
  'not-received': {
    [PROVIDER]: {
      buttons: ['transition/provider-add-proof', 'transition/provider-refund-not-received'],
      nextStep: t => ({
        id: 'OrderNextStep.not-received.seller',
        dates: { respondBy: dataOf(t).notReceived?.respondBy || null },
      }),
    },
    [CUSTOMER]: {
      buttons: ['transition/customer-confirm-arrived'],
      nextStep: t => ({
        id: 'OrderNextStep.not-received.buyer',
        dates: { respondBy: dataOf(t).notReceived?.respondBy || null },
      }),
    },
  },
  disputed: {
    [CUSTOMER]: {
      buttons: ['transition/customer-add-dispute-info', 'transition/customer-close-dispute'],
      nextStep: () => ({ id: 'OrderNextStep.disputed.buyer' }),
    },
    [PROVIDER]: {
      buttons: ['transition/provider-respond-dispute', 'transition/provider-refund'],
      nextStep: () => ({ id: 'OrderNextStep.disputed.seller' }),
    },
  },
  'on-hold': {
    [CUSTOMER]: { buttons: [], nextStep: () => ({ id: 'OrderNextStep.on-hold.buyer' }) },
    [PROVIDER]: { buttons: [], nextStep: () => ({ id: 'OrderNextStep.on-hold.seller' }) },
  },
  'return-initiated': {
    [CUSTOMER]: {
      buttons: [
        'transition/customer-mark-return-shipped',
        'transition/customer-buy-return-label',
        // One button: the server sends the order back where the return came
        // from (customer-cancel-return or -to-completed) and voids the label.
        'transition/customer-cancel-return',
      ],
      when: {
        'transition/customer-buy-return-label': t =>
          !hasReturnLabel(t) && dataOf(t).returnShippingPaidBy !== 'seller',
      },
      returnShipping: true,
      nextStep: t => ({
        id: hasReturnLabel(t)
          ? 'OrderNextStep.return-initiated.buyerWithLabel'
          : dataOf(t).returnShippingPaidBy === 'seller'
          ? 'OrderNextStep.return-initiated.buyerSellerLabel'
          : 'OrderNextStep.return-initiated.buyer',
        dates: { shipBy: dataOf(t).returnShipBy || null },
      }),
    },
    [PROVIDER]: {
      buttons: ['transition/provider-send-return-label'],
      when: {
        'transition/provider-send-return-label': t => !hasReturnLabel(t),
      },
      returnShipping: true,
      nextStep: t => ({
        id: hasReturnLabel(t)
          ? 'OrderNextStep.return-initiated.sellerWithLabel'
          : 'OrderNextStep.return-initiated.seller',
        dates: { shipBy: dataOf(t).returnShipBy || null },
      }),
    },
  },
  'return-shipped': {
    [PROVIDER]: {
      // Received: the order's own items decide final or partial (the server picks).
      buttons: [
        'transition/provider-mark-return-received',
        'transition/return-received-partial',
        'transition/provider-reject-return',
      ],
      when: { 'transition/return-received-partial': () => false },
      returnShipping: true,
      nextStep: t => {
        const { deliveredAt: delivered, inspectionDeadline } = getReturnShipping(t);
        return delivered
          ? {
              id: 'OrderNextStep.return-shipped.sellerDelivered',
              dates: { deadline: inspectionDeadline },
            }
          : { id: 'OrderNextStep.return-shipped.seller' };
      },
    },
    [CUSTOMER]: {
      buttons: [],
      returnShipping: true,
      nextStep: t => {
        const { deliveredAt: delivered, inspectionDeadline } = getReturnShipping(t);
        return delivered
          ? {
              id: 'OrderNextStep.return-shipped.buyerDelivered',
              dates: { deadline: inspectionDeadline },
            }
          : { id: 'OrderNextStep.return-shipped.buyer' };
      },
    },
  },
  'return-rejected': {
    // The seller's reason, as eBay and Reverb show it to the buyer.
    [CUSTOMER]: {
      buttons: [],
      returnShipping: true,
      nextStep: t => ({
        id: 'OrderNextStep.return-rejected.buyer',
        values: {
          hasReason: dataOf(t).returnSellerReview?.reason ? 'yes' : 'no',
          reason: dataOf(t).returnSellerReview?.reason || '',
        },
      }),
    },
    [PROVIDER]: {
      buttons: [],
      returnShipping: true,
      nextStep: () => ({ id: 'OrderNextStep.return-rejected.seller' }),
    },
  },
  canceled: {
    // Nothing to say when nothing was charged (a payment that never went through).
    [CUSTOMER]: {
      buttons: [],
      nextStep: t => (refundedToBuyer(t) ? { id: 'OrderNextStep.canceled.buyer' } : null),
    },
  },
  // Refunded in full: after a return, the seller can put what came back in stock.
  refunded: {
    [CUSTOMER]: { buttons: [], nextStep: () => ({ id: 'OrderNextStep.refunded.buyer' }) },
    [PROVIDER]: { buttons: [RELIST] },
  },
  chargeback: {
    [CUSTOMER]: { buttons: [], nextStep: () => ({ id: 'OrderNextStep.chargeback.buyer' }) },
    [PROVIDER]: { buttons: [], nextStep: () => ({ id: 'OrderNextStep.chargeback.seller' }) },
  },
};

// After the order is received: review and (buyer) return.
const afterReceipt = state => ({
  [CUSTOMER]: {
    buttons: ['review', CUSTOMER_RETURN_TRANSITION_BY_STATE[state]].filter(Boolean),
    when: { [CUSTOMER_RETURN_TRANSITION_BY_STATE[state]]: t => returnOpen(t) },
    nextStep: t => {
      const windows = returnWindows(t);
      if (isPickup(t) && (windows.preferenceOpen || windows.defectOpen)) {
        return {
          id: 'OrderNextStep.completed.buyerPickup',
          dates: {
            returnBy:
              windows.defectOpen && !windows.preferenceOpen
                ? windows.defectEndsAt
                : windows.preferenceEndsAt,
          },
        };
      }
      if (windows.preferenceOpen)
        return {
          id: 'OrderNextStep.completed.buyerReturn',
          dates: { returnBy: windows.preferenceEndsAt },
        };
      if (windows.defectOpen)
        return {
          id: 'OrderNextStep.completed.buyerDefect',
          dates: { returnBy: windows.defectEndsAt },
        };
      return null;
    },
  },
  // A return of some items of a cart, refunded, leaves the order completed.
  [PROVIDER]: { buttons: ['review', RELIST] },
});
['completed', 'reviewed-by-customer', 'reviewed-by-provider', 'reviewed'].forEach(state => {
  TABLE[state] = afterReceipt(state);
});

// A collected item is returned by arrangement, not by the parcel flow: no
// label or tracking can show it went back (TradMarket opens one if needed).
const isPickup = transaction => dataOf(transaction).deliveryMethod === 'pickup';

/** A return can be opened: a window is open and an item is left to send back. */
function returnOpen(transaction) {
  const windows = returnWindows(transaction);
  return (
    !isPickup(transaction) &&
    (windows.preferenceOpen || windows.defectOpen) &&
    itemsLeftToReturn(transaction)
  );
}

/** Has this side left a review, and may they still? */
const canReview = (state, role) =>
  role === CUSTOMER
    ? ['completed', 'reviewed-by-provider'].includes(state)
    : ['completed', 'reviewed-by-customer'].includes(state);

// ─────────────────────────── state data ───────────────────────────

/**
 * @param {Object} txInfo { transaction, transactionRole, nextTransitions }
 * @param {Object} processInfo { processName, processState, actionButtonProps, leaveReviewProps }
 */
export const getStateDataForPurchaseProcess = (txInfo, processInfo) => {
  const { transaction, transactionRole: role, nextTransitions } = txInfo;
  const { processName, processState, actionButtonProps, leaveReviewProps } = processInfo;
  const base = { processName, processState };

  // A question with no offer yet: nothing to do on the order itself.
  if (processState === 'inquiry') {
    return base;
  }
  // Orders from before the ledger: shown as they are, with nothing to do here.
  if (!isLedgerOrder(transaction)) {
    return {
      ...base,
      olderFormat: true,
      nextStep: { id: 'OrderNextStep.olderFormat' },
      buttons: [],
    };
  }

  const entry = TABLE[processState]?.[role] || {};
  const offered = new Set((nextTransitions || []).map(t => t.attributes.name));
  const allowed = name => {
    if (name === 'review') return canReview(processState, role) && !!leaveReviewProps;
    if (name === RELIST) return role === PROVIDER && canRelist(transaction);
    if (!offered.has(name)) return false;
    const when = entry.when?.[name];
    return when ? when(transaction) : true;
  };
  const buttons = (entry.buttons || [])
    .filter(allowed)
    .slice(0, 4)
    .map(name => (name === 'review' ? leaveReviewProps : actionButtonProps(name, role)));

  const returnLabel = returnDetails(transaction);
  return {
    ...base,
    buttons,
    nextStep: entry.nextStep ? entry.nextStep(transaction) : null,
    isPendingPayment: !!entry.isPendingPayment,
    returnShipping: entry.returnShipping ? getReturnShipping(transaction) : null,
    returnLabel,
    showReturnTracking: !!returnLabel,
    showReviews: ['reviewed'].includes(processState),
    showReviewAsFirstLink: processState === 'completed',
    showReviewAsSecondLink: ['reviewed-by-customer', 'reviewed-by-provider'].includes(processState),
  };
};

// For tests: the table itself (read only).
export const PURCHASE_TABLE = TABLE;
